// Public programmatic pages: /new/:profession and /new/:profession/:city
//
// SEO surface + inbound capture. Lists recently-added businesses in a
// category (public-record fields only) and lets owners claim their listing
// or add a business we don't have yet. Submissions go to the public inbound
// endpoint and are promoted into the lead database by the daily pipeline.

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { theme } from './theme';
import { PROFESSIONS, SIGNAL_BADGES } from './constants';
import { setPageSeo } from './seo';

const API = process.env.REACT_APP_API_URL || 'https://api.leadengine.amanar.ai';

const PUBLIC_CATEGORIES = PROFESSIONS.filter(
  (p) => p.key && p.key !== 'corporate'
);

const FEATURED_CITIES = [
  'Miami', 'Orlando', 'Tampa', 'Jacksonville', 'St Petersburg', 'Fort Lauderdale',
];

const label = (key) =>
  (PROFESSIONS.find((p) => p.key === key) || {}).label || key;

const titleCase = (s) =>
  (s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// ── Inbound / claim form ────────────────────────────────────────────────────
const InboundForm = ({ profession, prefill, onDone }) => {
  const [form, setForm] = useState({
    business_name: prefill?.name || '',
    city: prefill?.city ? titleCase(prefill.city) : '',
    phone: '', email: '', website: '', message: '', consent: false,
  });
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');
  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      const res = await fetch(`${API}/api/v1/public/inbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          profession,
          entity_id: prefill?.id || null,
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          company_url: e.target.company_url.value, // honeypot
        }),
      });
      if (res.ok) { setState('done'); return; }
      const d = await res.json().catch(() => ({}));
      setError(d.detail || 'Submission failed — check phone/email and try again.');
      setState('error');
    } catch {
      setError('Network error — please try again.');
      setState('error');
    }
  };

  if (state === 'done') {
    return (
      <div style={{ padding: '18px', backgroundColor: theme.surfaceAlt, borderRadius: '8px',
        color: theme.heading, fontSize: '0.9rem' }}>
        ✅ Thanks! Your listing is being updated. Vendors that serve new businesses
        may reach out with relevant offers.
        <button onClick={onDone} style={{ marginLeft: '12px', background: 'none', border: 'none',
          color: theme.primary, cursor: 'pointer', fontSize: '0.85rem' }}>Close</button>
      </div>
    );
  }

  const input = {
    width: '100%', padding: '10px 12px', marginBottom: '10px', borderRadius: '6px',
    border: `1px solid ${theme.border}`, backgroundColor: theme.bg, color: theme.heading,
    boxSizing: 'border-box', fontSize: '0.9rem',
  };

  return (
    <form onSubmit={submit} style={{ padding: '18px', backgroundColor: theme.surfaceAlt,
      borderRadius: '8px', marginTop: '8px' }}>
      <input style={input} placeholder="Business name *" required value={form.business_name}
        onChange={set('business_name')} disabled={!!prefill} />
      <div style={{ display: 'flex', gap: '10px' }}>
        <input style={input} placeholder="City" value={form.city} onChange={set('city')} />
        <input style={input} placeholder="Phone" value={form.phone} onChange={set('phone')} />
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <input style={input} type="email" placeholder="Email" value={form.email} onChange={set('email')} />
        <input style={input} placeholder="Website" value={form.website} onChange={set('website')} />
      </div>
      <textarea style={{ ...input, minHeight: '60px', resize: 'vertical' }}
        placeholder="What is your business looking for? (optional — e.g. POS system, insurance, suppliers)"
        value={form.message} onChange={set('message')} />
      {/* Honeypot — hidden from humans, tempting to bots. */}
      <input name="company_url" tabIndex="-1" autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, border: 0 }} />
      <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', color: theme.textMute,
        fontSize: '0.78rem', marginBottom: '12px', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.consent} onChange={set('consent')} required />
        I agree that vendors and service providers may contact me about products and
        services for my business.
      </label>
      {state === 'error' && <p style={{ color: theme.danger, fontSize: '0.8rem' }}>{error}</p>}
      <button type="submit" disabled={state === 'sending'} style={{
        padding: '11px 22px', backgroundColor: theme.primary, color: '#fff', border: 'none',
        borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
      }}>{state === 'sending' ? 'Sending…' : prefill ? 'Claim this listing' : 'Add my business'}</button>
      <button type="button" onClick={onDone} style={{ marginLeft: '10px', background: 'none',
        border: 'none', color: theme.textMute, cursor: 'pointer', fontSize: '0.85rem' }}>Cancel</button>
    </form>
  );
};

// ── Do-not-contact / removal form ────────────────────────────────────────────
const RemovalForm = ({ onDone }) => {
  const [form, setForm] = useState({ phone: '', email: '', business_name: '' });
  const [state, setState] = useState('idle');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone && !form.email) return;
    setState('sending');
    try {
      const res = await fetch(`${API}/api/v1/public/remove`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, company_url: e.target.company_url.value }),
      });
      setState(res.ok ? 'done' : 'error');
    } catch { setState('error'); }
  };

  if (state === 'done') {
    return <p style={{ color: theme.accent, fontSize: '0.8rem', marginTop: '10px' }}>
      ✅ Received. That phone/email will be removed from listings and won’t appear in exports.
      <button onClick={onDone} style={{ marginLeft: '10px', background: 'none', border: 'none',
        color: theme.primary, cursor: 'pointer', fontSize: '0.8rem' }}>Close</button>
    </p>;
  }
  const input = { padding: '9px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`,
    backgroundColor: theme.bg, color: theme.heading, fontSize: '0.85rem' };
  return (
    <form onSubmit={submit} style={{ marginTop: '12px', padding: '14px', backgroundColor: theme.surface,
      borderRadius: '8px', border: `1px solid ${theme.border}`, display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
      <input style={{ ...input, flex: '1 1 150px' }} placeholder="Phone to remove" value={form.phone} onChange={set('phone')} />
      <input style={{ ...input, flex: '1 1 150px' }} placeholder="or email" value={form.email} onChange={set('email')} />
      <input name="company_url" tabIndex="-1" autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, border: 0 }} />
      <button type="submit" disabled={state === 'sending'} style={{ padding: '9px 16px', borderRadius: '6px',
        border: 'none', background: theme.primary, color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
        {state === 'sending' ? 'Sending…' : 'Request removal'}
      </button>
      {state === 'error' && <span style={{ color: theme.danger, fontSize: '0.78rem' }}>Enter a phone or email.</span>}
    </form>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────
export default function PublicListings() {
  const { profession = 'food', city } = useParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);   // entity being claimed
  const [adding, setAdding] = useState(false);      // "add my business" form
  const [removing, setRemoving] = useState(false);  // do-not-contact form

  const cityName = city ? titleCase(city.replace(/-/g, ' ')) : null;
  const catLabel = label(profession);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/v1/public/listings?profession=${profession}&limit=100`;
      if (cityName) url += `&city=${encodeURIComponent(cityName)}`;
      const res = await fetch(url);
      const data = await res.json();
      setListings(Array.isArray(data) ? data : []);
    } catch { setListings([]); }
    setLoading(false);
  }, [profession, cityName]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    const where = cityName ? `${cityName}, FL` : 'Florida';
    const month = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
    setPageSeo({
      title: `New ${catLabel} in ${where} — ${month} | Amanar Lead Engine`,
      description: `Recently opened and newly licensed ${catLabel.toLowerCase()} in ${where}, updated daily from Florida state licensing records. Own one? Claim your listing.`,
      canonical: `https://leadengine.amanar.ai${window.location.pathname}`,
    });
  }, [catLabel, cityName]);

  // A profession/city page with nothing behind it is a 200 that looks like an
  // error — exactly what Google reports as a soft 404. Ask for it to be left
  // out of the index rather than counted against the site. Only decided once
  // the fetch has settled, so "still loading" is never mistaken for "empty".
  useEffect(() => {
    if (loading) return;
    setPageSeo({ robots: listings.length === 0 ? 'noindex, follow' : 'index, follow' });
  }, [loading, listings.length]);

  const where = cityName ? `${cityName}, Florida` : 'Florida';

  return (
    // data-listings-state is the contract scripts/prerender.js waits on: it
    // captures the DOM only once this stops saying "loading", so prerendered
    // pages always contain the real listings rather than the spinner.
    <div data-listings-state={loading ? 'loading' : listings.length ? 'ready' : 'empty'}
      style={{ backgroundColor: theme.bg, minHeight: '100vh', color: theme.text,
      fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '32px 20px' }}>
        <header style={{ marginBottom: '8px' }}>
          <Link to="/" style={{ color: theme.primary, textDecoration: 'none', fontWeight: 700,
            letterSpacing: '1px', fontSize: '0.85rem' }}>AMANAR LEAD ENGINE</Link>
          <h1 style={{ color: theme.heading, fontSize: '1.7rem', margin: '14px 0 6px' }}>
            New {catLabel} in {where}
          </h1>
          <p style={{ color: theme.textMute, fontSize: '0.92rem', maxWidth: '640px' }}>
            Updated daily from Florida state licensing records. Own one of these
            businesses? Claim your listing to correct details and tell vendors what
            you need — or{' '}
            <button onClick={() => { setAdding(true); setClaiming(null); }} style={{
              background: 'none', border: 'none', color: theme.primary, cursor: 'pointer',
              padding: 0, fontSize: '0.92rem', textDecoration: 'underline' }}>
              add your business</button>{' '}
            if it isn't here yet.
          </p>
        </header>

        {adding && (
          <InboundForm profession={profession} prefill={null} onDone={() => setAdding(false)} />
        )}

        {/* Category + city nav (internal links for SEO crawl paths) */}
        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '18px 0' }}>
          {PUBLIC_CATEGORIES.map((c) => (
            <Link key={c.key} to={`/new/${c.key}${city ? `/${city}` : ''}`} style={{
              padding: '5px 11px', borderRadius: '14px', fontSize: '0.75rem', textDecoration: 'none',
              color: c.key === profession ? '#fff' : theme.textMute,
              backgroundColor: c.key === profession ? theme.primary : theme.surface,
              border: `1px solid ${theme.border}`,
            }}>{c.label}</Link>
          ))}
        </nav>
        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          <Link to={`/new/${profession}`} style={{ padding: '5px 11px', borderRadius: '14px',
            fontSize: '0.75rem', textDecoration: 'none',
            color: !city ? '#fff' : theme.textMute,
            backgroundColor: !city ? theme.accent : theme.surface,
            border: `1px solid ${theme.border}` }}>All Florida</Link>
          {FEATURED_CITIES.map((c) => {
            const slug = c.toLowerCase().replace(/\s+/g, '-');
            return (
              <Link key={c} to={`/new/${profession}/${slug}`} style={{
                padding: '5px 11px', borderRadius: '14px', fontSize: '0.75rem', textDecoration: 'none',
                color: city === slug ? '#fff' : theme.textMute,
                backgroundColor: city === slug ? theme.accent : theme.surface,
                border: `1px solid ${theme.border}`,
              }}>{c}</Link>
            );
          })}
        </nav>

        {loading ? (
          <p style={{ color: theme.textMute, padding: '40px 0' }}>Loading…</p>
        ) : listings.length === 0 ? (
          <p style={{ color: theme.textMute, padding: '40px 0' }}>
            No recent listings here yet — check back tomorrow, this updates daily.
          </p>
        ) : (
          <div style={{ backgroundColor: theme.surface, borderRadius: '10px',
            border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
            {listings.map((l) => (
              <div key={l.id} style={{ padding: '13px 16px', borderBottom: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <span style={{ color: theme.heading, fontWeight: 600, fontSize: '0.95rem' }}>
                      {titleCase(l.name)}
                    </span>
                    {l.stage && SIGNAL_BADGES[l.stage] && (
                      <span style={{ marginLeft: '8px', padding: '2px 7px', borderRadius: '10px',
                        fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                        color: SIGNAL_BADGES[l.stage].fg, backgroundColor: SIGNAL_BADGES[l.stage].bg }}>
                        {SIGNAL_BADGES[l.stage].label}</span>
                    )}
                    <div style={{ color: theme.textMute, fontSize: '0.78rem', marginTop: '2px' }}>
                      {titleCase(l.city || '')}{l.county ? ` · ${l.county} County` : ''} · added {l.added}
                    </div>
                  </div>
                  <button onClick={() => { setClaiming(claiming?.id === l.id ? null : l); setAdding(false); }}
                    style={{ padding: '6px 13px', borderRadius: '6px', fontSize: '0.75rem',
                      border: `1px solid ${theme.primary}`, background: 'none', color: theme.primary,
                      cursor: 'pointer', fontWeight: 600 }}>
                    This is my business
                  </button>
                </div>
                {claiming?.id === l.id && (
                  <InboundForm profession={profession} prefill={claiming} onDone={() => setClaiming(null)} />
                )}
              </div>
            ))}
          </div>
        )}

        <footer style={{ marginTop: '28px', color: theme.textMute, fontSize: '0.75rem' }}>
          Data compiled from Florida DBPR and Division of Corporations public records.
          Listings refresh every business day. <Link to="/" style={{ color: theme.primary }}>
          Vendors: search all leads →</Link>
          <div style={{ marginTop: '10px' }}>
            Don’t want to be contacted?{' '}
            <button onClick={() => setRemoving(!removing)} style={{ background: 'none', border: 'none',
              color: theme.primary, cursor: 'pointer', padding: 0, fontSize: '0.75rem', textDecoration: 'underline' }}>
              Request removal</button>
            {removing && <RemovalForm onDone={() => setRemoving(false)} />}
          </div>
        </footer>
      </div>
    </div>
  );
}
